import { useAuth0 } from "@auth0/auth0-react";
import { Button, DatePicker, Dropdown, Modal } from "antd";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import {
  restoreOrg,
  restoreQboOrg,
  addOrg,
  addQboOrg,
  getRestoreEstimate,
} from "../../utils/organization";
import { getRestoreDemoVideo, checkPayrollAvailability, getXeroEmployees, uploadEmployeeCSV } from "../../utils/users";
import { useRef } from "react"; // Add useRef


const ErrorMessageComponent = ({ message }) => {
  if (message) {
    return (
      <div className={"pt-2"}>
        <p className={"text-red-500 font-bold"}>{message}</p>
      </div>
    );
  }
};

const AddRestoreOrgOrgModal = ({
  organizationId,
  organizationName,
  organizations,
  backupDates,
  onModalClose,
  onRestoreSubmit,
  initialBackupDate = null,
  initialTargetOrgId = null,
  initialTargetOrgName = null,
  skipVideo = false,
  type = "xero",
  sourceOrgCountryCode = null,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  // Some API payloads omit `provider`; default to the current restore flow provider.
  const orgProvider = (org) => org?.provider ?? (type === "qbo" ? "qbo" : "xero");
  const matchesProvider = (org) =>
    orgProvider(org) === (type === "qbo" ? "qbo" : "xero");

  /** API may send `subscription_status` (DB) without legacy `subscription`; treat inactive as ok for restore target. */
  const isRestoreTargetEligible = (org) => {
    const raw = org.subscription_status ?? org.subscription;
    if (raw == null || raw === "") {
      return org.org_status != 1;
    }
    return Number(raw) === 0;
  };

  const isVaultInactive = (org) => Number(org.is_vault_activated ?? 0) === 0;

  const [choosenItem, setChoosenItem] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [restoreOrgModalOpen, setRestoreOrgModalOpen] = useState(false);
  const [choosenBackupDate, setChoosenBackupDate] = useState("");
  const [restoreId, setRestoreId] = useState();
  const [formError, setFormError] = useState();

  const [searchQuery, setSearchQuery] = useState("");

  const [isButtonLoading, setIsButtonLoading] = useState(false); // Tracks the currently loading button
  const [isPayrollAvailable, setIsPayrollAvailable] = useState(false);
  const [isPayrollChecking, setIsPayrollChecking] = useState(false);
  const [payrollCountry, setPayrollCountry] = useState("");
  const [payrollMessage, setPayrollMessage] = useState("");
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadedSuccessfully, setCsvUploadedSuccessfully] = useState(false);
 
  const videoRef = useRef(null); // Create a ref

  const normalizePayrollCountry = (code) => {
    const normalized = String(code || "").trim().toUpperCase();
    return normalized === "UK" ? "GB" : normalized;
  };

  const resolvedPayrollCountry = normalizePayrollCountry(
    payrollCountry || sourceOrgCountryCode
  );

  // AU payroll restore requires encrypted TFN CSV before submit; NZ/GB use native payroll API restore.
  const requiresEmployeeTaxCsv = resolvedPayrollCountry === "AU";

  const [isVideoCompleted, setIsVideoCompleted] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);

  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);



  useEffect(() => {
    if (organizationName) {
      setRestoreOrgModalOpen(true);
      setFormError("");

      // If skipVideo is true, skip the video step
      if (skipVideo) {
        setIsVideoCompleted(true);
      } else {
        setIsVideoCompleted(false);
        fetchVideoUrl();
      }

      // Pre-fill backup date if provided
      if (initialBackupDate) {
        const parsedDate = dayjs(initialBackupDate, "YYYY-MM-DD");
        setChoosenBackupDate(parsedDate);
        // Check payroll availability and fetch estimate for the pre-filled date
        checkPayrollAvailabilityLocal(initialBackupDate);
        fetchEstimate(initialBackupDate);
      }

      // Pre-fill target organization if provided
      if (initialTargetOrgId && initialTargetOrgName) {
        setChoosenItem(initialTargetOrgName);
        setRestoreId(initialTargetOrgId);
      }
    }
  }, [organizationName, skipVideo, initialBackupDate, initialTargetOrgId, initialTargetOrgName]);
  
  const checkPayrollAvailabilityLocal = async (backupDate) => {
    try {
      setIsPayrollChecking(true);
      const token = await getIdTokenClaims();
      console.log("token", token);
      console.log("organizationId", organizationId);
      console.log("backupDate", backupDate);
      
      const response = await checkPayrollAvailability(token.__raw, organizationId, backupDate);
      
      console.log("Response status:", response.status);
      console.log("Response ok:", response.status === 200);
      
      if (response.status === 200) {
        const data = response.data;
        console.log("Full API response:", data);
        console.log("data type:", typeof data);
        console.log("data value:", data);
        
        // Check if data has a success property or if it's a boolean
        let isAvailable = false;
        if (typeof data === 'object' && data !== null) {
          isAvailable = data.success === true || data.hasPaymentModule === true;
        } else if (typeof data === 'boolean') {
          isAvailable = data === true;
        } else if (typeof data === 'number') {
          isAvailable = data === 1;
        } else if (typeof data === 'string') {
          isAvailable = data.trim().toLowerCase() === 'true';
        }
        
        console.log("Calculated isAvailable:", isAvailable);
        setIsPayrollAvailable(isAvailable);
        setPayrollCountry(
          typeof data === "object" && data !== null ? data.country || "" : ""
        );
        setPayrollMessage(
          typeof data === "object" && data !== null ? data.message || "" : ""
        );
      } else {
        console.log("Response not ok, status:", response.status);
        setIsPayrollAvailable(false);
        setPayrollCountry("");
        setPayrollMessage("");
      }
    } catch (error) {
      console.error('Error checking payroll availability:', error);
      setIsPayrollAvailable(false);
      setPayrollCountry("");
      setPayrollMessage("");
    } finally {
      setIsPayrollChecking(false);
    }
  };

  const downloadSampleCSV = async () => {
    try {
      // Check if backup date is selected
      if (!choosenBackupDate) {
        enqueueSnackbar('Please select a backup date first.', {
          variant: "error",
        });
        return;
      }

      setIsDownloadLoading(true);
      
      const token = await getIdTokenClaims();
      
      // Call your CodeIgniter backend API to get Xero employee data
      const response = await getXeroEmployees(token.__raw, organizationId, choosenBackupDate.format("YYYY-MM-DD"));
      
      if (response.status === 200) {
        const employeeData = response.data;
        
        // Create CSV content with headers and data
        let csvContent = "Employee ID,First Name,Last Name,TFN Number\n";
        
        // Add employee data rows
        if (employeeData.data && employeeData.data.length > 0) {
          employeeData.data.forEach(employee => {
            csvContent += `${employee.employeeId || ''},${employee.firstName || ''},${employee.lastName || ''},${employee.tfnNumber || ''}\n`;
          });
        } else {
          // If no data, add sample rows
          csvContent += "EMP001,John,Doe,\n";
          csvContent += "EMP002,Jane,Smith,\n";
        }
        
        // Create a blob with the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        
        // Create download link
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'employee_sample.csv');
        link.style.visibility = 'hidden';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
      } else {
        enqueueSnackbar('Failed to fetch employee data. Please try again.', {
          variant: "error",
        });
      }
      
    } catch (error) {
      console.error('Error fetching employee data:', error);
      enqueueSnackbar('Failed to fetch employee data. Please try again.', {
        variant: "error",
      });
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const isValidType = file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
    const isValidExtension = file.name.toLowerCase().endsWith('.csv');
    if (isValidType || isValidExtension) {
      setCsvFile(file);
    } else {
      enqueueSnackbar('Please select a valid CSV file.', {
        variant: "error",
      });
      event.target.value = '';
    }
  };

  const uploadCSV = async () => {
    if (!csvFile) {
      enqueueSnackbar('Please select a CSV file first.', {
        variant: "error",
      });
      return;
    }

    if (!choosenBackupDate) {
      enqueueSnackbar('Please select a backup date first.', {
        variant: "error",
      });
      return;
    }

    try {
      setIsUploadLoading(true);
      
      const token = await getIdTokenClaims();
      const formData = new FormData();
      formData.append('csv_file', csvFile);
      formData.append('backup_date', choosenBackupDate.format("YYYY-MM-DD"));
      formData.append('organization_id', organizationId);
      
      const response = await uploadEmployeeCSV(token.__raw, formData);
      
      if (response.status === 200) {
        const result = response.data;
        if (result.success) {
          enqueueSnackbar('CSV uploaded successfully!', {
            variant: "success",
          });
          setCsvUploadedSuccessfully(true);
          // Keep csvFile to show upload success, don't reset it
          // setCsvFile(null);
          // Reset file input
          const fileInput = document.getElementById('csv-file-input');
          if (fileInput) fileInput.value = '';
        } else {
          enqueueSnackbar(result.message || 'Failed to upload CSV.', {
            variant: "error",
          });
          setCsvUploadedSuccessfully(false);
        }
      } else {
        enqueueSnackbar('Failed to upload CSV. Please try again.', {
          variant: "error",
        });
        setCsvUploadedSuccessfully(false);
      }
      
    } catch (error) {
      console.error('Error uploading CSV:', error);
      enqueueSnackbar('Failed to upload CSV. Please try again.', {
        variant: "error",
      });
      setCsvUploadedSuccessfully(false);
    } finally {
      setIsUploadLoading(false);
    }
  };
  
  const fetchVideoUrl = async () => {
    try {
      setIsVideoLoading(true);
      const token = await getIdTokenClaims();
      const response = await getRestoreDemoVideo(token.__raw); // adjust if needed
      if (response.data.success) {
        setVideoUrl(response.data.url);
      } else {
        console.error("Failed to load video URL");
      }
    } catch (error) {
      console.error("Video fetch error", error);
    } finally {
      setIsVideoLoading(false);
    }
  };

  const fetchEstimate = async (date) => {
    if (!organizationId || !date) return;
    try {
      setEstimateLoading(true);
      setEstimate(null);
      const token = await getIdTokenClaims();
      const res = await getRestoreEstimate(token.__raw, organizationId, date);
      if (res.data.success) {
        setEstimate(res.data.estimate);
      } else {
        setEstimate(null);
      }
    } catch {
      setEstimate(null);
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleAddNewOrg = async () => {
    try {
      // Store pending restore state in sessionStorage
      const pendingState = {
        sourceOrgId: organizationId,
        sourceOrgName: organizationName,
        backupDate: choosenBackupDate?.format("YYYY-MM-DD"),
        backupDates: backupDates,
        existingOrgIds: organizations.map(o => o.id),
        timestamp: Date.now()
      };
      sessionStorage.setItem('pendingRestoreFlow', JSON.stringify(pendingState));

      // Redirect to the correct provider (Xero/QBO) to add a new organization
      const token = await getIdTokenClaims();
      const addNewOrg = type === "qbo" ? addQboOrg : addOrg;
      addNewOrg(token.__raw);

      // Modal will close automatically as page redirects
    } catch (error) {
      console.error('Error adding new organization:', error);
      // Clean up sessionStorage if there's an error
      sessionStorage.removeItem('pendingRestoreFlow');
      enqueueSnackbar('Failed to redirect to Xero. Please try again.', {
        variant: "error",
      });
    }
  };



  const getDisabledDates = (current) => {
    const allowedDates = backupDates.map((item) =>
      dayjs(item.rec_date, "YYYY-MM-DD")
    );

    // Disable all dates except those in allowedDates
    return !allowedDates.some((date) =>
      current.isSame(dayjs(date, "YYYY-MM-DD"), "day")
    );
  };

  // Filter organizations based on search query

  const renderDropdownList = (
    <div className="max-h-60 overflow-y-auto ">
      {/* Add new organization option - first item */}
      <div
        className="py-3 px-4 hover:bg-blue-50 cursor-pointer border-b border-gray-200 flex items-center gap-2 font-semibold text-blue-600"
        onClick={() => {
          setDropdownVisible(false);
          handleAddNewOrg();
        }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add new organization
      </div>

      {/* Existing organizations */}
      {organizations
        .filter(
          (org) =>
            org.account.toLowerCase().includes(searchQuery.toLowerCase()) &&
            isVaultInactive(org) &&
            isRestoreTargetEligible(org) &&
            matchesProvider(org)
        )
        .map((item, index) => (
          <div
            key={index}
            className={"py-3 px-4 hover:bg-gray-50 cursor-pointer "}
            onClick={() => {
              setChoosenItem(item.account);
              setRestoreId(item.id);
              setDropdownVisible(false);
            }}
          >
            {item.account}
          </div>
        ))}
    </div>
  );

  const handleFormSubmit = async () => {
    try {
      setIsButtonLoading(true); // ✅ Set loading state before processing
      console.log("set is button loading true");

      const token = await getIdTokenClaims();

      if (
        choosenBackupDate &&
        choosenBackupDate.format("YYYY-MM-DD") &&
        organizationId &&
        restoreId
      ) {
        const date = choosenBackupDate.format("YYYY-MM-DD");

        const restoreApi = type === "qbo" ? restoreQboOrg : restoreOrg;
        await restoreApi(token.__raw, organizationId, restoreId, date)
          .then((res) => {
            setIsButtonLoading(false); // ✅ Stop loading when request is done

            if (res.data.redirect_url) {
              window.location.href = res.data.redirect_url;
            } else if (res.data.success) {
              setRestoreOrgModalOpen(false);
              onModalClose();
              onRestoreSubmit();
            } else {
              enqueueSnackbar(res.data.message, {
                variant: "error",
              });
            }
          })
          .catch((err) => {
            console.error(err);
            enqueueSnackbar(
              err.response?.data?.message ||
                err.response?.data?.msg ||
                "Something went wrong",
              {
                variant: "error",
              }
            );
            setIsButtonLoading(false); // ✅ Stop loading on error
          });
      } else {
        setFormError("All fields are required");
        setIsButtonLoading(false); // ✅ Stop loading if validation fails
      }
    } catch (e) {
      console.error(e);
      setIsButtonLoading(false); // ✅ Ensure loading is reset in case of error
    }
  };

  const onCloseModal = () => {
    // Pause the video if it exists
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Optional: reset to beginning
    }
  
    setChoosenItem("");
    setDropdownVisible(false);
    setRestoreOrgModalOpen(false);
    setChoosenBackupDate(null);
    setRestoreId(null);
    setFormError(null);
    setCsvFile(null); // Reset CSV file
    setCsvUploadedSuccessfully(false); // Reset CSV upload success state
    setIsPayrollAvailable(false); // Reset payroll availability
    setIsPayrollChecking(false); // Reset payroll checking state
    setPayrollCountry("");
    setPayrollMessage("");
    setEstimate(null);
    setEstimateLoading(false);
    onModalClose();
  };

  return (
    <Modal
      title={<div className={"text-2xl"}> {isVideoCompleted ? 'Restore organization' : 'Please complete the tutorial first.'} </div>}
      centered
      open={restoreOrgModalOpen}
      footer={
        !isVideoCompleted ? (
          // Step 1: Show "I Understand, Continue" button
          <div className="flex items-center justify-end">
            <Button
              type="primary"
              className="bg-green-500 hover:bg-green-600 text-white rounded-lg px-8 py-2 h-auto text-base font-medium"
              onClick={() => {
                setIsVideoCompleted(true);
                // Check payroll if a date is already selected
                if (choosenBackupDate) {
                  checkPayrollAvailabilityLocal(choosenBackupDate.format("YYYY-MM-DD"));
                }
              }}
            >
              I Understand, Continue
            </Button>
          </div>
        ) : (
          // Step 2: Show Cancel and Confirm buttons
          <div className={"flex items-center justify-between gap-3"}>
            {/* Estimate — left side */}
            <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
              {estimateLoading ? (
                <div className="flex items-center gap-1.5 text-blue-500">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-500" />
                  <span>Estimating...</span>
                </div>
              ) : estimate ? (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    Est. <span className="font-medium text-gray-700">{estimate.total_human_readable}</span>
                  </span>
                  {estimate.spans_multiple_days && (
                    <span
                      title="Restore will pause overnight due to Xero's daily API limit"
                      className="text-orange-500 text-xs bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded"
                    >
                      multi-day
                    </span>
                  )}
                </div>
              ) : null}
            </div>

            {/* Cancel + Confirm — right side */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div
                key="cancel"
                className={
                  "px-2 text-sm py-1.5 border border-gray-200 rounded-lg cursor-pointer"
                }
                onClick={onCloseModal}
              >
                Cancel
              </div>

              <Button
                key="submit"
                type="primary"
                className={`rounded-lg ${
                  choosenBackupDate &&
                  (!isPayrollAvailable ||
                    !requiresEmployeeTaxCsv ||
                    csvUploadedSuccessfully)
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={handleFormSubmit}
                loading={isButtonLoading}
                disabled={
                  !choosenBackupDate ||
                  (isPayrollAvailable &&
                    requiresEmployeeTaxCsv &&
                    !csvUploadedSuccessfully)
                }
              >
                Confirm
              </Button>
            </div>
          </div>
        )
      }
      onCancel={onCloseModal}
    >
      {isVideoLoading ? (
        <div className="text-center text-gray-500 py-8">Loading video...</div>
      ) : !isVideoCompleted && videoUrl ? (
        <div>
          {/* Video Player */}
          <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg mb-6">
            <video
              src={videoUrl}
              ref={videoRef}
              controls
              className="absolute top-0 left-0 w-full h-full"
            />
          </div>

          {/* Instructions Section */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">i</span>
            </div>
            <div className="flex-1">
              <p className="text-gray-700 font-medium mb-3">Please follow these instructions before proceeding to restore:</p>
              <div className="text-gray-600 text-sm space-y-2">
                <p>a) Ensure your organization is a fresh organization and not restoring to a current organization.</p>
                <p>b) Make sure that you turn off the reminders for due invoices after restore completes immediately.</p>
                <p>c) If restore runs into some issues, then contact us on <a href="mailto:support@wowzertech.com" className="text-blue-600 hover:underline">support@wowzertech.com</a>.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
         <ErrorMessageComponent message={formError} />
        <div className={"py-6 text-gray-500 text-sm"}>
          <div className={"text-sm text-gray-700 mb-4"}>
            <p className="font-medium mb-2">Backup Date</p>
            <DatePicker
              disabledDate={getDisabledDates}
              onChange={(date) => {
                setChoosenBackupDate(date);
                // Reset payroll state when date changes
                setIsPayrollAvailable(false);
                setIsPayrollChecking(false);
                setPayrollCountry("");
                setPayrollMessage("");
                setCsvUploadedSuccessfully(false); // Reset CSV upload success when date changes
                // Check payroll availability and fetch estimate for the new date
                if (date) {
                  const formatted = date.format("YYYY-MM-DD");
                  checkPayrollAvailabilityLocal(formatted);
                  fetchEstimate(formatted);
                }
              }}
              className={"w-full py-2.5 border border-gray-300 rounded-lg"}
              value={choosenBackupDate}
              placeholder="Select date"
              dateRender={(currentDate) => {
                // `getDisabledDates` presumably returns `true` if a date should be disabled
                const isDisabled = getDisabledDates(currentDate);

                // If it's NOT disabled, we give it a green background.
                if (!isDisabled) {
                  return (
                    <div
                      className="ant-picker-cell-inner"
                      style={{
                        backgroundColor: "#1cd446",
                        borderRadius: "50%", // or remove if you don't want round shape
                        color: "#fff", // ensure text is visible
                      }}
                    >
                      {currentDate.date()}
                    </div>
                  );
                }

                // Default rendering for disabled dates
                return (
                  <div className="ant-picker-cell-inner">
                    {currentDate.date()}
                  </div>
                );
              }}
            />
          </div>

          {/* Payroll Module Status - Show after date selection */}
          {choosenBackupDate && (
            <div className="mb-4">
              {isPayrollChecking ? (
                <div className="flex items-center gap-2 text-blue-600 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Checking payroll availability...
                </div>
              ) : isPayrollAvailable ? (
                // Payroll Module Detected - Success Box (Green)
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">!</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">Payroll Module Detected</p>
                      <p className="text-gray-600 text-sm">
                        {payrollMessage ||
                          (requiresEmployeeTaxCsv
                            ? "Employee tax information needs to be updated"
                            : "Master payroll data will be included in restore")}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                // No Payroll Module - Warning Box (Orange)
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">No Payroll Module</p>
                      <p className="text-gray-600 text-sm">Backup can be restored directly</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CSV Management Section - AU payroll only (TFN upload) */}
          {isPayrollAvailable && requiresEmployeeTaxCsv && choosenBackupDate && (
            <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
              <p className="font-medium mb-3 text-gray-800">Update Employee Tax Information</p>
              
              <div className="space-y-4">
                {/* Download CSV */}
                <div className="text-center">
                  <Button
                    type="default"
                    size="middle"
                    className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 flex justify-center items-center gap-2 py-2"
                    onClick={downloadSampleCSV}
                    loading={isDownloadLoading}
                    disabled={!choosenBackupDate}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download CSV
                  </Button>
                </div>

                {/* Upload CSV */}
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    Upload updated CSV with Tax-IDs 
                    {!csvFile && <span className="text-red-500 ml-1">*</span>}
                    {!csvFile && (
                      <span className="text-xs text-red-500 block">Required when payroll module is detected</span>
                    )}
                  </p>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    csvFile 
                      ? 'border-green-300 bg-green-50 hover:border-green-400' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    <input
                      type="file"
                      id="csv-file-input"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <label htmlFor="csv-file-input" className="cursor-pointer block">
                      <svg className="mx-auto h-10 w-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm text-gray-600">Drop files here or click to browse</p>
                      <p className="text-xs text-gray-500">Max size: 1MB</p>
                    </label>
                  </div>
                  
                  {csvFile && (
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {csvFile.name}
                      </span>
                      <Button
                        type="primary"
                        size="small"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={uploadCSV}
                        loading={isUploadLoading}
                        disabled={!choosenBackupDate}
                      >
                        Upload CSV
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={"text-sm text-gray-700 mb-4"}>
            <p className="font-medium mb-2">Sync Data From</p>
            <input
              type="text"
              className={
                "rounded-lg px-3 py-2.5 border border-gray-300 mt-1 w-full bg-gray-50"
              }
              value={organizationName}
              disabled={true}
            />
          </div>

          <div className={"text-sm text-gray-700 mb-4"}>
            <p className="font-medium mb-2">Sync Data To</p>
            <Dropdown
              open={dropdownVisible}
              onOpenChange={(visible) => setDropdownVisible(visible)}
              placement="bottomCenter"
              trigger={["click"]}
              className={"w-full"}
              dropdownRender={(menu) => (
                <div
                  className={
                    "bg-white w-full border border-gray-300 rounded-lg shadow-lg"
                  }
                >
                  <div className={"border-b border-gray-300 p-2"}>
                    <div
                      className={
                        "flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2"
                      }
                    >
                      <ReactSVG
                        src={"/assets/icons/search.svg"}
                        className={"text-gray-400"}
                      />
                      <input
                        type="text"
                        className={"outline-0 bg-transparent w-full"}
                        placeholder={"Search by title or description..."}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"mt-2"}>{renderDropdownList}</div>
                </div>
              )}
            >
              <div
                className={
                  "w-full cursor-pointer p-2.5 flex justify-between items-center rounded-lg border-gray-300 border text-medium bg-white"
                }
              >
                <div className={"font-medium text-sm"}>
                  {choosenItem || "Select organization"}
                </div>
                <ReactSVG
                  src="/assets/icons/common-dropdown.svg"
                  className={"text-gray-700"}
                />
              </div>
            </Dropdown>
          </div>
        </div>

        </>
      )}
    </Modal>
  );
};

export default AddRestoreOrgOrgModal;
